// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2026 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { typography } from "@recidiviz/design-system";
import { rem } from "polished";
import { useCallback, useMemo } from "react";
import { ResponsiveOrdinalFrame } from "semiotic";
import styled from "styled-components";

import { CARAS_COMPONENT_NAMES, ParoleRiskAssessment } from "~datatypes";
import { palette } from "~design-system";

import { EdgeAwareTooltip, useChartContainer } from "./EdgeAwareTooltip";
import {
  ChartColumn,
  ChartTitle,
  ChartWrapper,
  DEFAULT_CHART_HEIGHT,
  TOOL_COLORS,
} from "./RiskAssessmentSection.styles";

const ComponentList = styled.ol`
  margin: 0;
  padding: 0;
  list-style: none;
  counter-reset: component;
`;

const ComponentListItem = styled.li`
  ${typography.Sans14}
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  counter-increment: component;

  &::before {
    content: counter(component);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: ${rem(20)};
    height: ${rem(20)};
    border-radius: 50%;
    background: ${palette.slate10};
    color: ${palette.slate70};
    font-size: 11px;
  }
`;

const SUBCATEGORY_LABEL_MAX_CHARS_PER_LINE = 18;
const SUBCATEGORY_LABEL_LINE_HEIGHT = 12;

function wrapLabelText(text: string): Array<string> {
  const words = text.split(" ");
  const lines: Array<string> = [];
  let currentLine = "";
  for (const word of words) {
    const candidate = currentLine ? `${currentLine} ${word}` : word;
    if (
      candidate.length > SUBCATEGORY_LABEL_MAX_CHARS_PER_LINE &&
      currentLine
    ) {
      lines.push(currentLine);
      currentLine = word;
    } else {
      currentLine = candidate;
    }
  }
  if (currentLine) lines.push(currentLine);
  return lines;
}

/**
 * Renders one bar's category label as wrapped SVG text, centered on its row.
 *
 * Hoisted to module scope so React doesn't see an unstable component
 * recreated every render. The tooltip callbacks below can't be, since they
 * need each render's own barChartBounds.
 *
 * semiotic types `oLabel`'s render overload as returning a DOM `Element`
 * rather than a React element, so the cast below is a real interop gap. It
 * lives here rather than at every `oLabel=` call site.
 *
 * @param name - The subcategory name, which semiotic types as `unknown`.
 */
function renderSubcategoryBarLabel(name?: unknown): string | Element {
  const lines = wrapLabelText(name as string);
  // Centers the wrapped block on the bar's row: first line shifts up by
  // half the block's height, each next line steps down one line height.
  const firstLineOffset =
    (-(lines.length - 1) / 2) * SUBCATEGORY_LABEL_LINE_HEIGHT;
  return (
    <text textAnchor="end" style={{ fontSize: 11, fill: palette.slate70 }}>
      {lines.map((line, i) => (
        <tspan
          key={line}
          x={0}
          dy={i === 0 ? firstLineOffset : SUBCATEGORY_LABEL_LINE_HEIGHT}
        >
          {line}
        </tspan>
      ))}
    </text>
  ) as unknown as Element;
}

/**
 * One assessment's breakdown: a numbered component list for CARAS, a
 * signed-contribution bar chart for CARAS elsewhere, or a score-out-of-max
 * bar chart for every other tool.
 *
 * @param assessment - The assessment to break down.
 * @param showCarasComponentList - Render CARAS as the fixed list of its
 *   components, whatever the assessment carries, rather than the bar chart.
 *   Comes from the tenant's riskAssessmentConfig.
 * @param title - Heading above the chart. Empty renders none, for a caller
 *   that captions the chart itself.
 * @param height - Overrides DEFAULT_CHART_HEIGHT, which is sized for the
 *   trajectory panel rather than a card.
 */
export function SubcategoryBreakdownChart({
  assessment,
  showCarasComponentList,
  title = "Subcategory Breakdown (Most recent assessment)",
  height,
}: {
  assessment: ParoleRiskAssessment;
  showCarasComponentList: boolean;
  title?: string;
  height?: number;
}) {
  const {
    setContainer: setBarChart,
    bounds: barChartBounds,
    containerRef: barChartRef,
  } = useChartContainer();

  const subcategoryBarData = useMemo(
    () =>
      assessment.subcategories?.map((s) => ({
        name: s.name,
        score: s.score,
        remaining: s.maxScore - s.score,
      })),
    [assessment],
  );

  const carasFactorData = useMemo(
    () =>
      assessment.carasFactors?.map((f) => ({
        name: f.name,
        value: f.value,
        coefficient: f.coefficient,
        contribution: f.value * f.coefficient,
      })),
    [assessment],
  );

  const renderSubcategoryBarTooltip = useCallback(
    (d: {
      pieces?: Array<{ name: string; score: number; remaining: number }>;
    }): JSX.Element | null => {
      const point = d.pieces?.[0];
      if (!point) return null;
      return (
        <EdgeAwareTooltip containerRef={barChartRef} resetKey={point.name}>
          <div>{point.name}</div>
          <div>
            Score: {point.score} / {point.score + point.remaining}
          </div>
        </EdgeAwareTooltip>
      );
    },
    [barChartRef],
  );

  // CARAS scores via coefficients, not points out of a max, so its bars
  // show each item's signed *contribution* rather than a score/max fraction.
  const renderCarasFactorTooltip = useCallback(
    (d: {
      pieces?: Array<{
        name: string;
        value: number;
        coefficient: number;
        contribution: number;
      }>;
    }): JSX.Element | null => {
      const point = d.pieces?.[0];
      if (!point) return null;
      return (
        <EdgeAwareTooltip containerRef={barChartRef} resetKey={point.name}>
          <div>{point.name}</div>
          <div>Value: {point.value}</div>
          <div>
            Contribution: {point.contribution >= 0 ? "+" : ""}
            {point.contribution.toFixed(2)}
          </div>
        </EdgeAwareTooltip>
      );
    },
    [barChartRef],
  );

  if (assessment.tool === "CARAS") {
    if (showCarasComponentList) {
      return (
        <ChartColumn>
          <ChartTitle>Individual Components of the CARAS</ChartTitle>
          <ChartWrapper>
            <ComponentList>
              {CARAS_COMPONENT_NAMES.map((name) => (
                <ComponentListItem key={name}>{name}</ComponentListItem>
              ))}
            </ComponentList>
          </ChartWrapper>
        </ChartColumn>
      );
    }

    if (!carasFactorData) return null;
    type CarasFactorDatum = (typeof carasFactorData)[number];
    const carasFactorSummary = carasFactorData
      .map(
        (f) =>
          `${f.name} contribution ${f.contribution >= 0 ? "+" : ""}${f.contribution.toFixed(2)}`,
      )
      .join(", ");
    return (
      <ChartColumn>
        {title && <ChartTitle>{title}</ChartTitle>}
        <ChartWrapper
          $height={height}
          ref={setBarChart}
          role="img"
          aria-label={`Subcategory breakdown for CARAS: ${carasFactorSummary}`}
        >
          {barChartBounds.width > 0 && (
            <ResponsiveOrdinalFrame
              // Remounts on tool switch so semiotic doesn't try to animate
              // between two unrelated bar sets, which briefly overlaps them.
              key={assessment.tool}
              responsiveWidth={false}
              size={[
                barChartBounds.width,
                barChartBounds.height || height || DEFAULT_CHART_HEIGHT,
              ]}
              data={carasFactorData}
              type="bar"
              projection="horizontal"
              oAccessor="name"
              oPadding={12}
              rAccessor="contribution"
              style={(d: CarasFactorDatum) => ({
                fill: d.contribution >= 0 ? TOOL_COLORS.CARAS : palette.slate30,
                rx: 3,
              })}
              margin={{ left: 220, right: 8, top: 8, bottom: 30 }}
              oLabel={renderSubcategoryBarLabel}
              axes={[
                {
                  orient: "bottom",
                  ticks: 5,
                  tickFormat: (n: number) => n.toFixed(2),
                  className: "score-axis",
                  baseline: "under",
                },
                {
                  orient: "left",
                  tickFormat: () => "",
                  className: "category-axis",
                },
              ]}
              hoverAnnotation
              optimizeCustomTooltipPosition
              tooltipContent={renderCarasFactorTooltip}
            />
          )}
        </ChartWrapper>
      </ChartColumn>
    );
  }

  if (!subcategoryBarData) return null;
  const subcategoryBarSummary = subcategoryBarData
    .map((s) => `${s.name} ${s.score} of ${s.score + s.remaining}`)
    .join(", ");

  return (
    <ChartColumn>
      {title && <ChartTitle>{title}</ChartTitle>}
      <ChartWrapper
        $height={height}
        ref={setBarChart}
        role="img"
        aria-label={`Subcategory breakdown for ${assessment.tool}: ${subcategoryBarSummary}`}
      >
        {barChartBounds.width > 0 && (
          <ResponsiveOrdinalFrame
            // See the CARAS branch above for why this is keyed by tool.
            key={assessment.tool}
            responsiveWidth={false}
            size={[
              barChartBounds.width,
              barChartBounds.height || height || DEFAULT_CHART_HEIGHT,
            ]}
            data={subcategoryBarData}
            type="bar"
            projection="horizontal"
            oAccessor="name"
            oPadding={12}
            rAccessor="score"
            style={() => ({
              fill: TOOL_COLORS[assessment.tool],
              rx: 3,
            })}
            margin={{ left: 140, right: 8, top: 8, bottom: 30 }}
            oLabel={renderSubcategoryBarLabel}
            axes={[
              {
                orient: "bottom",
                ticks: 5,
                tickFormat: (n: number) => `${n}`,
                className: "score-axis",
                baseline: "under",
              },
              {
                orient: "left",
                tickFormat: () => "",
                className: "category-axis",
              },
            ]}
            hoverAnnotation
            optimizeCustomTooltipPosition
            tooltipContent={renderSubcategoryBarTooltip}
          />
        )}
      </ChartWrapper>
    </ChartColumn>
  );
}
